import { ActionMenu, Box } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { AccessResponse } from '@fullstory/proto/fs/shared/accesscontrol';
import { LibraryObjectType } from '@fullstory/routes';
import { assertExhaustive } from '@fullstory/typeutils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledAccessDeniedMessage } from '../AccessDeniedMessage';
import { DxObjectHeader, DxObjectHeaderProps } from '../DxObjectHeader';
import { SaveAndShareModal } from '../SaveAndShareModal';
import { TitleForm } from '../TitleForm';

export interface DxObjectHeaderWrapperProps
  extends Omit<
    DxObjectHeaderProps,
    | 'ownedBy'
    | 'canUpdate'
    | 'renameModalOpen'
    | 'setRenameModalOpen'
    | 'modalState'
    | 'setModalState'
    | 'saveDisabled'
  > {
  access?: AccessResponse | null;
  additionalMenuItems?: React.ComponentPropsWithoutRef<typeof ActionMenu>['actions'];
  onAdditionalMenuItemSelect?: React.ComponentPropsWithoutRef<typeof ActionMenu>['onSelect'];
  onArchive: (id: string) => void;
  onValidate: () => boolean;
  onChange: (title: string, desc?: string) => void;
  showTitleForm?: boolean;
}

export const DxObjectHeaderWrapper = ({
  access,
  objectType,
  visibility = Visibility.UNSET,
  additionalMenuItems,
  values,
  onAdditionalMenuItemSelect,
  onArchive,
  children,
  onValidate,
  onChange,
  showTitleForm = false,
  onSave,
  canCreate,
  ...rest
}: DxObjectHeaderWrapperProps) => {
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const { errors } = rest;
  const [modalState, setModalState] = useState<
    React.ComponentProps<typeof SaveAndShareModal>['action'] | undefined
  >(undefined);

  useEffect(() => {
    if (errors) {
      setModalState(undefined);
    }
  }, [errors]);

  const saveBtnClicked = () => {
    setSaveAttempted(true);
    return onValidate();
  };

  const owner = useMemo(() => access?.owners ?? [], [access]);
  const createdBy = values?.createdBy ?? '';

  const canDuplicate = useMemo(
    () => access?.canCreate || (canCreate && rest.isCanned),
    [access?.canCreate, canCreate, rest.isCanned],
  );

  const menuItems = useMemo(
    () =>
      (additionalMenuItems ?? []).concat(
        {
          id: 'duplicate',
          label: intl.formatMessage({ defaultMessage: 'Duplicate', id: '4fHiNl' }),
          iconName: 'duplicate',
          disabled: !canDuplicate,
          tooltip: !canDuplicate ? (
            <StyledAccessDeniedMessage viewOnly suggestion="none" />
          ) : undefined,
        },
        {
          id: 'rename',
          label: intl.formatMessage({ defaultMessage: 'Rename', id: 'iXNbPf' }),
          iconName: 'pencil',
          disabled: !access?.canUpdate,
          tooltip: !access?.canUpdate && (
            <StyledAccessDeniedMessage
              viewOnly={!access?.canUpdate && !access?.canCreate}
              isCanned={rest.isCanned}
              suggestion={access?.canCreate ? 'duplicate' : 'none'}
            />
          ),
        },
        {
          id: 'archive',
          label: intl.formatMessage({ defaultMessage: 'Archive', id: 'hrgo+E' }),
          iconName: 'folder',
          disabled: !access?.canUpdate,
          tooltip: !access?.canUpdate && (
            <StyledAccessDeniedMessage viewOnly isCanned={rest.isCanned} suggestion="none" />
          ),
        },
      ),
    [access?.canCreate, access?.canUpdate, additionalMenuItems, canDuplicate, rest.isCanned],
  );

  const onMenuItemSelect = useCallback(
    (id: string) => {
      onValidate();
      switch (id) {
        case 'duplicate':
          setModalState('duplicate');
          break;
        case 'rename':
          setRenameModalOpen(true);
          break;
        case 'archive':
          onArchive?.(values?.id ?? '');
          break;
        default:
          onAdditionalMenuItemSelect?.(id);
      }
    },
    [onAdditionalMenuItemSelect, onArchive, values?.id, onValidate],
  );

  return (
    <>
      <Box margin={24}>
        <DxObjectHeader
          ownedBy={owner}
          canUpdate={access?.canUpdate ?? false}
          canCreate={canCreate}
          objectType={objectType}
          values={
            values
              ? {
                  ...values,
                  createdBy: rest.isCanned
                    ? createdBy.replace('FullStory', 'Fullstory')
                    : values.createdBy,
                }
              : undefined
          }
          visibility={visibility}
          renameModalOpen={renameModalOpen}
          setRenameModalOpen={setRenameModalOpen}
          modalState={modalState}
          setModalState={setModalState}
          dropdownMenuItems={menuItems}
          onDropdownMenuItemsSelect={onMenuItemSelect}
          onValidate={saveBtnClicked}
          onSave={onSave}
          saveDisabled={saveDisabled}
          {...rest}
        >
          {children}
        </DxObjectHeader>
      </Box>
      {canCreate && showTitleForm && (
        <TitleForm
          itemId={itemIdByType(objectType)}
          title={values?.name ?? ''}
          placeholder={getTitlePlaceholder(objectType)}
          description={values?.description}
          onChange={onChange}
          setSaveDisabled={setSaveDisabled}
          saveClicked={saveAttempted}
        />
      )}
    </>
  );
};

function getTitlePlaceholder(type: LibraryObjectType) {
  switch (type) {
    case ObjectType.DASHBOARD:
      return intl.formatMessage({ defaultMessage: 'Untitled Dashboard', id: 'L3ROcX' });
    case ObjectType.FUNNEL:
      return intl.formatMessage({ defaultMessage: 'Untitled Funnel', id: 'z8wMxj' });
    case ObjectType.HEATMAP:
      return intl.formatMessage({ defaultMessage: 'Untitled Heatmap', id: 'NPYcQ0' });
    case ObjectType.JOURNEY:
      return intl.formatMessage({ defaultMessage: 'Untitled Journey', id: 'xkWzBH' });
    case ObjectType.METRIC:
      return intl.formatMessage({ defaultMessage: 'Untitled Metric', id: 'V3wYKd' });
    case ObjectType.RETENTION:
      return intl.formatMessage({ defaultMessage: 'Untitled Retention', id: 'YpQcSJ' });
    case ObjectType.SEGMENT:
      return intl.formatMessage({ defaultMessage: 'Untitled Segment', id: 'SEl0X6' });
    case ObjectType.DATA_TABLE:
      return intl.formatMessage({ defaultMessage: 'Untitled Table', id: 'vHRK0y' });
    default:
      assertExhaustive(type);
  }
}

function itemIdByType(type: LibraryObjectType) {
  switch (type) {
    case ObjectType.DASHBOARD:
      return 'dashboard';
    case ObjectType.FUNNEL:
      return 'funnel';
    case ObjectType.HEATMAP:
      return 'heatmap';
    case ObjectType.JOURNEY:
      return 'journey-map';
    case ObjectType.METRIC:
      return 'metric';
    case ObjectType.RETENTION:
      return 'retention-metric';
    case ObjectType.SEGMENT:
      return 'segment';
    case ObjectType.DATA_TABLE:
      return 'data-table';
    default:
      assertExhaustive(type);
  }
}
